import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { FileText, Plus, Copy, Trash2, Edit, Search, Sparkles } from "lucide-react";
import PageHeader from "../../components/PageHeader";

const TIPOS = [
  { value: "contrato", label: "Contratos" },
  { value: "cotizacion", label: "Cotizaciones" },
  { value: "hoja_operativa", label: "Hojas operativas" },
  { value: "formulario", label: "Formularios" },
  { value: "checklist", label: "Checklists" },
  { value: "pdf_cliente", label: "PDF para clientes" },
  { value: "pdf_interno", label: "PDF internos" },
  { value: "whatsapp", label: "Mensajes WhatsApp" },
  { value: "correo", label: "Correos" },
  { value: "terminos", label: "Términos y condiciones" },
  { value: "reglamento", label: "Reglamentos" },
];

const Plantillas = () => {
  const navigate = useNavigate();
  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const [search, setSearch] = useState("");
  const [crearDialog, setCrearDialog] = useState(false);
  const [nueva, setNueva] = useState({ nombre: "", tipo: "contrato", tipo_evento: "", descripcion: "" });

  const fetchPlantillas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroTipo !== "todos") params.tipo = filtroTipo;
      const res = await axios.get(`${API}/plantillas`, { params });
      setPlantillas(res.data || []);
    } catch {
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, [filtroTipo]);

  useEffect(() => { fetchPlantillas(); }, [fetchPlantillas]);

  const handleCrear = async () => {
    if (!nueva.nombre.trim()) {
      toast.error("Asigna un nombre");
      return;
    }
    try {
      const res = await axios.post(`${API}/plantillas`, {
        ...nueva,
        bloques: [
          {
            tipo: "documento",
            orden: 0,
            props: {
              texto: `${nueva.nombre.toUpperCase()}\n\nEscribe aquí tu plantilla como en Word.\n\nUsa variables dinámicas: {{cliente}}, {{evento}}, {{fecha}}, {{total}}, {{anticipo}}, {{saldo}}, {{adultos}}, {{ninos}}, {{festejado}}, {{direccion}}, {{horario}}.\n\nDos saltos de línea = nuevo párrafo.\n\n— ${nueva.tipo_evento || "Tu equipo"}`
            }
          },
        ],
      });
      toast.success("Plantilla creada — empieza a escribir como en Word");
      setCrearDialog(false);
      setNueva({ nombre: "", tipo: "contrato", tipo_evento: "", descripcion: "" });
      navigate(`/panel/plantillas/${res.data.plantilla_id}`);
    } catch {
      toast.error("Error al crear");
    }
  };

  const handleDuplicar = async (id) => {
    try {
      const res = await axios.post(`${API}/plantillas/${id}/duplicar`);
      toast.success("Plantilla duplicada");
      fetchPlantillas();
      navigate(`/panel/plantillas/${res.data.plantilla_id}`);
    } catch {
      toast.error("Error al duplicar");
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm("¿Desactivar esta plantilla? Quedará en el historial pero ya no aparecerá activa.")) return;
    try {
      await axios.delete(`${API}/plantillas/${id}`);
      toast.success("Plantilla desactivada");
      fetchPlantillas();
    } catch {
      toast.error("Error");
    }
  };

  const filtradas = plantillas.filter(p => {
    if (!p.activa) return false;
    if (search && !p.nombre?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const tipoLabel = (t) => TIPOS.find(x => x.value === t)?.label || t;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={FileText}
        title="Plantillas"
        subtitle="Tipo Word: escribe libremente y usa variables. Sin constructores técnicos."
        actions={
          <Button onClick={() => setCrearDialog(true)} className="btn-primary" data-testid="btn-crear-plantilla">
            <Plus className="w-4 h-4 mr-2" /> Nueva plantilla
          </Button>
        }
      />

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
          <Input placeholder="Buscar plantilla..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 border-stone-300" data-testid="search-plantillas" />
        </div>
        <Select value={filtroTipo} onValueChange={setFiltroTipo}>
          <SelectTrigger className="w-full md:w-56 border-stone-300" data-testid="filter-tipo-plantilla">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los tipos</SelectItem>
            {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtradas.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <h3 className="font-display text-2xl text-stone-900 mb-2">Tu lienzo en blanco</h3>
            <p className="text-stone-500 text-sm max-w-md mx-auto mb-4">
              No tienes plantillas en este tipo. Crea la primera y diséñala bloque por bloque con variables dinámicas.
            </p>
            <Button onClick={() => setCrearDialog(true)} className="btn-primary" data-testid="btn-crear-plantilla-empty">
              <Plus className="w-4 h-4 mr-2" /> Crear plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtradas.map(p => (
            <Card key={p.plantilla_id} className="border-stone-200 hover:border-emerald-700 transition" data-testid={`card-plantilla-${p.plantilla_id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg text-stone-900 truncate">{p.nombre}</h3>
                    <p className="text-xs text-stone-500 mt-0.5">{tipoLabel(p.tipo)}{p.tipo_evento ? ` · ${p.tipo_evento}` : ""}</p>
                  </div>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px]">{(p.bloques || []).length} bloques</span>
                </div>
                {p.descripcion && <p className="text-xs text-stone-500 line-clamp-2">{p.descripcion}</p>}
                <div className="flex items-center gap-2 pt-2">
                  <Button size="sm" onClick={() => navigate(`/panel/plantillas/${p.plantilla_id}`)} className="btn-primary text-xs flex-1" data-testid={`btn-editar-${p.plantilla_id}`}>
                    <Edit className="w-3 h-3 mr-1" /> Editar
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDuplicar(p.plantilla_id)} className="border-stone-200" title="Duplicar" data-testid={`btn-dup-${p.plantilla_id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleEliminar(p.plantilla_id)} className="border-red-200 text-red-600 hover:bg-red-50" title="Desactivar" data-testid={`btn-del-${p.plantilla_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Crear Dialog */}
      <Dialog open={crearDialog} onOpenChange={setCrearDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Nueva plantilla</DialogTitle>
            <DialogDescription>Defínele un nombre y tipo. Después la editas bloque por bloque.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input value={nueva.nombre} onChange={(e) => setNueva(n => ({ ...n, nombre: e.target.value }))} placeholder="Ej: Contrato de bodas premium" className="border-stone-300" data-testid="input-nombre-plantilla" />
            </div>
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select value={nueva.tipo} onValueChange={(v) => setNueva(n => ({ ...n, tipo: v }))}>
                <SelectTrigger className="border-stone-300" data-testid="select-tipo-plantilla"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIPOS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de evento (opcional)</Label>
              <Input value={nueva.tipo_evento} onChange={(e) => setNueva(n => ({ ...n, tipo_evento: e.target.value }))} placeholder="Ej: boda, XV, corporativo" className="border-stone-300" />
            </div>
            <div className="space-y-2">
              <Label>Descripción (opcional)</Label>
              <Textarea value={nueva.descripcion} onChange={(e) => setNueva(n => ({ ...n, descripcion: e.target.value }))} rows={2} className="border-stone-300" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCrearDialog(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleCrear} className="btn-primary" data-testid="btn-confirmar-crear-plantilla">Crear y editar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Plantillas;
